import { UPLOAD_SETTLING_EXPIRED_CODE } from "./retryPolicy";
import { readUploadFacadeErrorCode } from "./uploadFacadeErrors";

/**
 * The one SETTLING verdict this ceiling bounds.
 *
 * `SETTLING_FACADE_ERROR_CODES` holds three codes, but only this one is answered by the target
 * ADMISSION gate (`uploadSessionRegistry.throwActiveTargetAdmissionConflict`), where the owner
 * being waited on may be a session that never finishes settling. The other two are answered by an
 * attempt the client itself owns and are resolved by the same request landing, so they carry no
 * comparable wedge risk and are deliberately left on the unbounded optimistic path.
 */
export const SETTLING_CONFLICT_ERROR_CODE = "UPLOAD_OPERATION_IN_PROGRESS";

/**
 * How long a durable item may keep being answered `UPLOAD_OPERATION_IN_PROGRESS` before its lane
 * stops believing the server and parks it.
 *
 * LSP-19912 made that verdict RETRYABLE (`classifyUploadFailure` -> `refresh`, i.e. retried
 * forever, by design) for every caller of the shared registry helper — contact-admin's rotation
 * lane, contact-async's photo lane, contact-live's snapshot lane, and any future one. That
 * unbounded loop is only safe while the server's answer stays truthful, and it stops being
 * truthful at the read model's anti-entropy repair window (`uploadSessionReadModel.ts`
 * `captureGraceMs`, 120 minutes): once the parent domain row has been quiet that long, the repair
 * pass infers the registry row to `ready` from the parent's own pre-existing `complete` status
 * WITHOUT checking that this attempt's finalizer ever ran, and a same-idempotencyId replay after
 * that point is answered `already-complete` — a false success for bytes that were never installed.
 *
 * Thirty minutes allows the server's twelve-minute completion reservation to settle. This is a
 * terminal-state deadline, independent of the 30-second interval between retries.
 */
export const SETTLING_CONFLICT_CEILING_MS = 30 * 60_000;

/**
 * The durable clock the ceiling is measured against, owned by whichever store the host lane
 * persists its items in.
 *
 * It is deliberately a port rather than a concrete table: the three lanes that reach the admission
 * gate persist their items in three different places (contact-admin's Dexie `smallFileUploads`,
 * contact-async's Dexie `photoSnapshots`, contact-live's own IndexedDB outbox), and the ONE thing
 * the ceiling actually requires of all of them is that the window survives a reload. An in-memory
 * marker cannot express this: the failure mode being bounded is precisely a loop that outlives the
 * lane instance that started it, so a per-instance clock restarts exactly when it must not.
 */
export interface SettlingWindowPort {
	/** The instant this item's settling window opened, or `undefined` when it has never opened. */
	read(): Promise<number | undefined>;
	/** Persists the window's opening instant durably. */
	arm(openedAt: number): Promise<void>;
	/**
	 * Discards the durable window, so the NEXT settling verdict opens a fresh one.
	 *
	 * Required rather than optional on purpose: a host that can arm the window but not close it
	 * measures every future settling episode from an instant that has already elapsed, which is the
	 * defect `withSettlingCeiling` exists to prevent. Every host persists the window as one nullable
	 * column, so clearing it is the same write that armed it with no value.
	 */
	disarm(): Promise<void>;
}

/** The client-minted terminal verdict. `retryPolicy` classifies it terminal; see the code's docs. */
export function settlingConflictExpiredError(elapsedMs: number): Error & { code: string } {
	return Object.assign(
		new Error(`Upload target was still reported as settling after ${Math.round(elapsedMs / 1000)}s [${UPLOAD_SETTLING_EXPIRED_CODE}]`),
		{ code: UPLOAD_SETTLING_EXPIRED_CODE }
	);
}

export interface SettlingCeilingOptions {
	/** The item's durable window. Omit the whole call when the host has no durable store. */
	window: SettlingWindowPort;
	ceilingMs?: number;
	now?: () => number;
	/** Reported once, when the ceiling converts a retryable verdict into a terminal one. */
	onExpired?: (details: { elapsedMs: number; ceilingMs: number; openedAt: number }) => void;
	/** Reported when an armed window is closed by a later success. See {@link withSettlingCeiling}. */
	onSettled?: (details: { elapsedMs: number; openedAt: number }) => void;
	/**
	 * This item's window as the CALLER already knows it, from the same durable row read that drove
	 * this attempt — `undefined` meaning "this row has no window open".
	 *
	 * Purely an I/O elision, and the reason it is worth having: `withSettlingCeiling` runs on the
	 * SUCCESS path of every healthy upload, where a window is almost never armed, and the lanes it
	 * wraps are sensitive to how long the gap between "target minted" and "target committed" is —
	 * an extra durable round trip there is long enough for a second drain to pick the same item up
	 * and mint a second server attempt. With this hint the healthy path performs no store access at
	 * all; only an item that actually saw a conflict pays for the read it skips and the write that
	 * closes it.
	 *
	 * Omit it when the caller has no such value in hand; the close then reads the window itself.
	 */
	knownOpenedAt?: () => number | undefined;
	/** Reported when the durable window could not be read, armed or disarmed — the verdict is left retryable. */
	onWindowError?: (error: unknown) => void;
}

/**
 * Enforces {@link SETTLING_CONFLICT_CEILING_MS} on the server's settling verdict, returning the
 * error the caller should actually throw.
 *
 * Everything about the rule lives here rather than in each lane, because all three of its edges are
 * easy to get subtly wrong per copy:
 *
 *  - the window is measured from the FIRST settling verdict this item saw, not this attempt's;
 *  - it is only ever consulted while the CURRENT failure is another settling verdict, so an
 *    interleaved network flap neither resets the clock nor terminalizes the item on its own;
 *  - a stored instant in the FUTURE is clock skew, not a longer window — re-arm from now rather
 *    than letting a skewed write make the ceiling unreachable (`poisonBudget`'s same rule);
 *  - a durable-store failure must never UPGRADE a retryable verdict into a terminal one. The next
 *    drain re-observes the same conflict and re-arms the window.
 */
export async function boundSettlingConflict(error: unknown, options: SettlingCeilingOptions): Promise<unknown> {
	if (readUploadFacadeErrorCode(error) !== SETTLING_CONFLICT_ERROR_CODE) return error;
	const ceilingMs = options.ceilingMs ?? SETTLING_CONFLICT_CEILING_MS;
	const observedAt = options.now ? options.now() : Date.now();
	let openedAt: number;
	try {
		openedAt = await resolveSettlingWindowStart(options.window, observedAt);
	} catch (windowError) {
		options.onWindowError?.(windowError);
		return error;
	}
	const elapsedMs = Math.max(0, observedAt - openedAt);
	if (elapsedMs < ceilingMs) return error;
	options.onExpired?.({ elapsedMs, ceilingMs, openedAt });
	return settlingConflictExpiredError(elapsedMs);
}

/**
 * Runs the admission-gated operation under the ceiling: bounds a settling verdict when it fails,
 * and CLOSES the durable window when it succeeds.
 *
 * The second half is the whole reason this wrapper exists rather than each host try/catching
 * `boundSettlingConflict` itself. `arm` is written on the FIRST settling verdict and, before this,
 * nothing ever unwrote it on a later success. Every item class reaching the admission gate re-mints
 * its target many times over its life — `recover-lost`, an expired presigned URL, a superseded
 * target, a fresh take — so the common shape is: conflict (window armed) -> the owner actually
 * finishes -> the next presign succeeds -> hours later a brand-new, unrelated conflict is measured
 * from the STALE instant and mints `UPLOAD_SETTLING_EXPIRED` immediately, parking a session whose
 * settling episode had only just begun.
 *
 * Only a SUCCESS closes the window, never a non-settling failure. A success is positive evidence
 * that the admission gate let this item through, so the episode the window was measuring is
 * provably over. A network flap says nothing about the owner it was waiting on, and resetting the
 * clock on one would let an item alternating conflict/flap evade the ceiling forever — the
 * invariant `boundSettlingConflict` already documents and is tested for.
 *
 * A disarm failure is reported and swallowed: bookkeeping must never fail an upload that the server
 * has already admitted. The window then stays armed, which is the pre-existing behavior, and the
 * next success closes it.
 */
export async function withSettlingCeiling<T>(operation: () => Promise<T>, options: SettlingCeilingOptions): Promise<T> {
	let result: T;
	try {
		result = await operation();
	} catch (error) {
		throw await boundSettlingConflict(error, options);
	}
	await closeSettlingWindow(options);
	return result;
}

/**
 * Discards an armed window after a success, touching the durable store only when one is open —
 * see `knownOpenedAt` for why the healthy path must stay free of store access.
 */
async function closeSettlingWindow(options: SettlingCeilingOptions): Promise<void> {
	try {
		const openedAt = options.knownOpenedAt ? options.knownOpenedAt() : await options.window.read();
		if (!isWindowInstant(openedAt)) return;
		await options.window.disarm();
		const settledAt = options.now ? options.now() : Date.now();
		options.onSettled?.({ elapsedMs: Math.max(0, settledAt - openedAt), openedAt });
	} catch (windowError) {
		options.onWindowError?.(windowError);
	}
}

function isWindowInstant(value: number | undefined): value is number {
	return typeof value === "number" && Number.isFinite(value);
}

/** Returns the durable start of this item's settling window, arming it on the first observation. */
async function resolveSettlingWindowStart(window: SettlingWindowPort, observedAt: number): Promise<number> {
	const armedAt = await window.read();
	if (typeof armedAt === "number" && Number.isFinite(armedAt) && armedAt <= observedAt) return armedAt;
	await window.arm(observedAt);
	return observedAt;
}
